import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { Invoice } from '@/lib/invoices';

const POLL_MS = 4000;

/**
 * T231: `loan_settings.contract_waits_invoice` — the customer's contract (step 8) opens only after the
 * merchant → Hisob e-invoice (step 7) is approved. While `active`, the case's invoice is polled until it is.
 * With the setting off `waiting` is always false and nothing is polled.
 */
export function useContractInvoiceGate(caseId: string | number | null, active: boolean) {
  const { data: settings } = useQuery({
    queryKey: ['/platform-settings/loan_settings'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/platform-settings/{key}', {
        params: { path: { key: 'loan_settings' } },
      });
      if (error) throw error;
      return (data?.data?.value ?? {}) as { contract_waits_invoice?: boolean };
    },
  });
  const enabled = settings?.contract_waits_invoice === true;

  const { data: invoice } = useQuery({
    queryKey: ['contract-invoice-gate', caseId],
    enabled: enabled && !!caseId && active,
    refetchInterval: query => (query.state.data?.status === 'approved' ? false : POLL_MS),
    queryFn: async (): Promise<Invoice | null> => {
      const { data, error, response } = await apiClient.GET('/murabaha/cases/{case}/invoice', {
        params: { path: { case: Number(caseId) } },
      });
      // the invoice is queued asynchronously after the sale — none yet is not an error
      if (response.status === 404) return null;
      if (error) throw error;
      return (data as unknown as { data: Invoice }).data;
    },
  });

  return { waiting: enabled && invoice?.status !== 'approved', invoice: invoice ?? null };
}
